import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  LinearProgress,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";

export interface CouponActionProgress {
  summary: string;
  percent: number;
  finished: boolean;
  success: boolean;
}

interface CouponActionPopupProps {
  open: boolean;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  onCancel: () => void;
  onConfirm: () => void;
  loading?: boolean;
  confirmDisabled?: boolean;
  error?: string | null;
  children?: React.ReactNode;
  progress?: CouponActionProgress | null;
  onDismiss: () => void;
}

/**
 * Confirm dialog used only by the coupons page.
 * Delete all, Expire all, and Reactivate all keep this same dialog open and show the job bar inside it.
 *
 * @param props.open - Whether the dialog is visible.
 * @param props.title - Heading, such as "Delete coupons".
 * @param props.message - The confirm question. Hidden once a bulk job is showing.
 * @param props.confirmText - Label on the confirm button, including Deleting, Expiring, or Reactivating.
 * @param props.cancelText - Label on the cancel button.
 * @param props.onCancel - Closes the dialog without writing. Ignored while a job is on screen.
 * @param props.onConfirm - Runs the coupon action.
 * @param props.loading - True while the confirm request is in flight and the job bar is not up yet.
 * @param props.confirmDisabled - Disables confirm before a required date is chosen.
 * @param props.error - Validation or request error shown under the message.
 * @param props.children - Extra fields, such as the expiry date input.
 * @param props.progress - Bulk job bar. Null for a normal confirm.
 * @param props.onDismiss - Closes the dialog after a failed bulk job.
 * @returns The coupons confirm dialog.
 */
const CouponActionPopup: React.FC<CouponActionPopupProps> = ({
  open,
  title,
  message,
  confirmText = "Apply",
  cancelText = "Cancel",
  onCancel,
  onConfirm,
  loading = false,
  confirmDisabled = false,
  error = null,
  children,
  progress = null,
  onDismiss,
}) => {
  const theme = useTheme();
  const c = theme.palette.customColors;
  const jobRunning = Boolean(progress && !progress.finished);
  const jobFailed = Boolean(progress?.finished && !progress?.success);
  const jobDone = Boolean(progress?.finished && progress?.success);
  const locked = loading || jobRunning || jobDone;
  const canClose = !locked || jobFailed;
  const buttonSx = { minWidth: 140 };

  if (!open) return null;

  return (
    <Dialog
      open
      onClose={locked ? undefined : jobFailed ? onDismiss : onCancel}
      aria-labelledby="coupon-action-dialog-title"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: c.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: `0 20px 40px ${c.shadowMedium}`,
          overflow: "hidden",
          maxWidth: { xs: "90vw", sm: "400px" },
          width: { xs: "90vw", sm: "400px" },
          margin: { xs: 2, sm: 4 },
          background: `linear-gradient(135deg, ${c.white} 0%, ${c.pageBackground} 100%)`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: theme.palette.orange.main,
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={jobFailed ? onDismiss : onCancel}
          disabled={!canClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
            color: c.greyTextColor,
            "&:hover": {
              backgroundColor: c.lightGreyColor,
              color: c.blackBorder,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          id="coupon-action-dialog-title"
          sx={{
            padding: { xs: "24px 24px 16px", sm: "32px 32px 20px" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: c.lightOrange,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 12px ${c.shadowLight}`,
            }}
          >
            <WarningAmberIcon
              sx={{ fontSize: 32, color: theme.palette.orange.main }}
            />
          </Box>
          <CustomTypography
            variant="heading4"
            color={theme.palette.orange.dark}
          >
            {title || "Are you sure?"}
          </CustomTypography>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: { xs: "0 24px 24px", sm: "0 32px 32px" },
            textAlign: "center",
            width: "100%",
          }}
        >
          {progress ? (
            <Box>
              <CustomTypography
                variant="body1"
                color={jobFailed ? c.errorRed : c.text}
              >
                {progress.summary}
              </CustomTypography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mt: 2,
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={progress.percent}
                  sx={{
                    height: 10,
                    width: 200,
                    borderRadius: 5,
                    backgroundColor: c.greyBackground,
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 5,
                      backgroundColor: progress.finished
                        ? jobFailed
                          ? c.errorRed
                          : theme.palette.success.main
                        : c.purple,
                    },
                  }}
                />
                <CustomTypography variant="body2" color={c.greyTextColor}>
                  {progress.percent}%
                </CustomTypography>
              </Box>
            </Box>
          ) : (
            <>
              <CustomTypography variant="body1" color={c.text}>
                {message}
              </CustomTypography>
              {children ? (
                <Box sx={{ mt: 2, width: "100%", textAlign: "left" }}>
                  {children}
                </Box>
              ) : null}
              <InlineFormError message={error || undefined} />
            </>
          )}
        </DialogContent>

        {progress && !jobFailed ? null : (
          <DialogActions
            sx={{
              padding: { xs: "0 24px 24px", sm: "0 32px 32px" },
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            {jobFailed ? (
              <AppButton
                variant="grayscale"
                label="Close"
                onClick={onDismiss}
                customStyles={buttonSx}
              />
            ) : (
              <>
                <AppButton
                  variant="grayscale"
                  label={cancelText}
                  onClick={onCancel}
                  disabled={loading}
                  customStyles={buttonSx}
                />
                <AppButton
                  variant="primary"
                  label={
                    loading ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress
                          size={18}
                          sx={{ color: c.white }}
                        />
                        {confirmText}
                      </Box>
                    ) : (
                      confirmText
                    )
                  }
                  onClick={onConfirm}
                  disabled={loading || confirmDisabled}
                  customStyles={buttonSx}
                />
              </>
            )}
          </DialogActions>
        )}
      </Box>
    </Dialog>
  );
};

export default CouponActionPopup;
